import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api, { usersApi } from '../../services/api';
import UTCTime from '../../components/UTCTime/UTCTime';
import QuickActions from '../../components/QuickActions/QuickActions';
import {
  FileText, Handshake, AlertTriangle, CheckCircle, Truck, Star, Clock,
  Users, Building2, Plane, Ship, Heart, Settings as SettingsIcon,
  ChevronRight, RefreshCw, Eye, EyeOff, ExternalLink, Bell, Lock,
  Megaphone, MessageSquare, PartyPopper, Search, Rocket, Mail, Info,
  ShieldAlert, Globe, Calendar
} from 'lucide-react';
import HelpTooltip from '../../components/HelpTooltip/HelpTooltip';
import './Dashboard.css';

interface DashboardHomeData {
  infrastructure: {
    assets: number;
    treatmentFacilities: number;
    ucpmExitHubs: number;
    ucpmTransitHubs: number;
  };
  requestMatrix: Record<string, Record<string, { count: number; patients: number }>>;
  categories: string[];
  statuses: string[];
  nationUsers: Array<{
    id: string;
    username: string | null;
    email: string;
    role: string;
    isNation: boolean;
    isEU: boolean;
    isAdmin: boolean;
    isExcon: boolean;
    isPlanner: boolean;
    isHub: boolean;
    lastLoginAt: string | null;
    flagImage: string | null;
  }>;
  assets: Array<{
    id: string;
    name: string;
    type: string;
    capacitySeated: number;
    capacityLying: number;
    capacityIntensive: number;
  }>;
}

interface DashboardNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  REQUEST_CREATED: <FileText size={14} />,
  REQUEST_PUBLISHED: <Megaphone size={14} />,
  REQUEST_CLOSED: <Lock size={14} />,
  REQUEST_COMPLETED: <CheckCircle size={14} />,
  COMMENT_ADDED: <MessageSquare size={14} />,
  OFFER_RECEIVED: <Handshake size={14} />,
  OFFER_ACCEPTED: <PartyPopper size={14} />,
  STATUS_CHANGED: <RefreshCw size={14} />,
  SELECTION_STARTED: <Search size={14} />,
  MISSION_CREATED: <Rocket size={14} />
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  DRAFT: 'status.draft',
  UNDER_REVIEW: 'status.review',
  PUBLISHED: 'status.published',
  UNDER_SELECTION: 'status.selection',
  ACCEPTED: 'status.accepted',
  PLANNED: 'status.planned',
  IN_TRANSIT: 'status.transit',
  COMPLETED: 'status.completed',
  CLOSED: 'status.closed'
};

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  MEDEVAC: 'categories.medevac',
  PERSONNEL: 'categories.personnel',
  MATERIAL: 'categories.equipment',
  OTHER: 'categories.other',
  NEWS: 'categories.news'
};

const ASSET_TYPE_LABEL_KEYS: Record<string, string> = {
  TRAIN: 'assetTypes.train',
  SHIP: 'assetTypes.ship',
  AIRCRAFT: 'assetTypes.aircraft',
  BUS: 'assetTypes.bus',
  AMBULANCE: 'assetTypes.ambulance',
  DRONE: 'assetTypes.drone',
  HELICOPTER: 'assetTypes.helicopter',
  FERRY: 'assetTypes.ferry',
  OTHER: 'assetTypes.other'
};

interface HelpdeskConfig {
  title: string;
  email: string;
  phone: string;
  info: string;
}

function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [helpdesk, setHelpdesk] = useState<HelpdeskConfig>({
    title: 'EU ERCC Helpdesk',
    email: 'ERCC-DUTY-OFFICER@ec.europa.eu',
    phone: '+32 2 29 21111',
    info: '24/7 Duty Officer'
  });
  const [reallifeSupport, setReallifeSupport] = useState<HelpdeskConfig>({
    title: 'Real Life Support',
    email: '',
    phone: '',
    info: ''
  });
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [expandedNotification, setExpandedNotification] = useState<string | null>(null);
  const [participatingNations, setParticipatingNations] = useState<{ code: string; name: string; flagImage: string; hasUser: boolean }[]>([]);
  const isVisibleRef = useRef(true);

  const getStatusLabel = (status: string): string => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? t(key) : status;
  };

  const getCategoryLabel = (category: string): string => {
    const key = CATEGORY_LABEL_KEYS[category];
    return key ? t(key) : category;
  };

  const getAssetTypeLabel = (type: string): string => {
    const key = ASSET_TYPE_LABEL_KEYS[type];
    return key ? t(key) : type;
  };

  useEffect(() => {
    loadData();
    loadHelpdesk();
    loadReallifeSupport();
    loadNotifications();
    loadParticipatingNations();

    // Visibility-based polling: pause when tab is hidden
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const notifInterval = setInterval(() => {
      if (isVisibleRef.current) loadNotifications();
    }, 15000);

    return () => {
      clearInterval(notifInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/statistics/dashboard/home');
      setData(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadHelpdesk = async () => {
    try {
      const response = await api.get('/api/settings/public/helpdesk');
      setHelpdesk(response.data);
    } catch {
      // Keep defaults
    }
  };

  const loadReallifeSupport = async () => {
    try {
      const response = await api.get('/api/settings/public/reallife-support');
      setReallifeSupport(response.data);
    } catch {
      // Keep defaults
    }
  };

  const loadNotifications = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications', {
        params: { limit: 50 }
      });
      setNotifications(response.data.notifications || []);
    } catch {
      // Silent fail
    }
  }, []);

  const loadParticipatingNations = async () => {
    try {
      const response = await usersApi.getNations();
      // Only show nations that actually have users in the DB
      setParticipatingNations(response.data.filter(n => n.hasUser));
    } catch {
      // Silent fail
    }
  };

  const markNotificationRead = async (id: string) => {
    try {
      await api.patch(`/api/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
    } catch {
      // Silent fail
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const unread = notifications.filter(n => !n.isRead);
      await Promise.all(unread.map(n => api.patch(`/api/notifications/${n.id}/read`)));
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // Silent fail
    }
  };

  const openNotifSettings = () => {
    navigate('/notification-settings');
  };

  // Quick action counts
  const pendingReviewCount = data ? (data.requestMatrix['MEDEVAC']?.['UNDER_REVIEW']?.count || 0)
    + (data.requestMatrix['PERSONNEL']?.['UNDER_REVIEW']?.count || 0)
    + (data.requestMatrix['MATERIAL']?.['UNDER_REVIEW']?.count || 0)
    + (data.requestMatrix['OTHER']?.['UNDER_REVIEW']?.count || 0) : 0;

  const myDraftCount = 0; // Would need user-specific query
  const activeMissionCount = 0; // Would need missions API call

  const formatLastLogin = (dateStr: string | null | undefined) => {
    if (!dateStr) return t('common.never');
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Map raw role string to descriptive label
  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'ADMIN': return t('roles.admin');
      case 'EXCON': return t('roles.excon');
      case 'EU': return t('roles.eu');
      case 'PLANNER': return t('roles.planner');
      case 'HUB': return t('roles.hub');
      case 'NATION': return t('roles.nation');
      default: return role;
    }
  };

  const getGreeting = (): string => {
    const hour = new Date().getUTCHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  // Determine the current user's primary descriptive role
  const getUserPrimaryRole = (): string => {
    if (user?.isAdmin) return t('roles.systemAdmin');
    if (user?.isExcon) return t('roles.excon');
    if (user?.isEU) return t('roles.eu');
    if (user?.isPlanner) return t('roles.transportPlanner');
    if (user?.isHub) return t('roles.hub');
    return t('roles.nation');
  };

  // Determine the dashboard title based on primary role
  const getDashboardRoleTitle = (): string => {
    if (user?.isAdmin) return t('dashboard.systemAdministration');
    if (user?.isExcon) return t('dashboard.exerciseControlTitle');
    if (user?.isEU) return t('dashboard.euCoordination');
    if (user?.isPlanner) return t('dashboard.transportPlanningTitle');
    if (user?.isHub) return t('dashboard.hubManagement');
    return user?.nation || t('roles.nation');
  };

  // Get the role icon for the dashboard welcome banner
  const getRoleIcon = (): React.ReactNode | null => {
    if (user?.isAdmin) return <ShieldAlert size={28} />;
    if (user?.isExcon) return <Eye size={28} />;
    if (user?.isEU) return <Globe size={28} />;
    if (user?.isPlanner) return <Calendar size={28} />;
    if (user?.isHub) return <Building2 size={28} />;
    return null; // NATION users use flag image
  };

  const getRoleBadges = (u: DashboardHomeData['nationUsers'][0]) => {
    const roles: string[] = [];
    if (u.isAdmin) roles.push('Admin');
    if (u.isExcon) roles.push('ExCon');
    if (u.isEU) roles.push('EU');
    if (u.isNation) roles.push('Nation');
    if (u.isPlanner) roles.push('Planner');
    if (u.isHub) roles.push('Hub');
    if (roles.length === 0) roles.push(getRoleLabel(u.role));
    return roles;
  };

  const getStatusTotal = (status: string): number => {
    if (!data) return 0;
    return data.categories.reduce((sum, cat) => {
      return sum + (data.requestMatrix[cat]?.[status]?.count || 0);
    }, 0);
  };

  const getCategoryTotal = (category: string): { count: number; patients: number } => {
    if (!data) return { count: 0, patients: 0 };
    return data.statuses.reduce(
      (sum, status) => {
        const cell = data.requestMatrix[category]?.[status];
        return {
          count: sum.count + (cell?.count || 0),
          patients: sum.patients + (cell?.patients || 0)
        };
      },
      { count: 0, patients: 0 }
    );
  };

  const getGrandTotal = (): number => {
    if (!data) return 0;
    return data.categories.reduce((sum, cat) => {
      return sum + getCategoryTotal(cat).count;
    }, 0);
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error || !data) {
    return <div className="error">{error || t('common.failedToLoad')}</div>;
  }

  return (
    <div className="dashboard-home">
      {/* Welcome & Last Login — full width */}
      <div className="dashboard-welcome">
        <div className="welcome-left">
          {getRoleIcon() ? (
            <div className="dashboard-role-icon">{getRoleIcon()}</div>
          ) : user?.flagImage ? (
            <img src={user.flagImage} alt={user.nation} className="welcome-flag" />
          ) : null}
          <div>
            <h2>{getGreeting()}, {user?.username || user?.nation}</h2>
            <p className="welcome-subtitle">
              {t('dashboard.title')} — {getDashboardRoleTitle()} | {getUserPrimaryRole()}
            </p>
          </div>
        </div>
        <div className="welcome-right">
          <div className="last-login">
            {t('dashboard.lastLogin')}: {formatLastLogin(user?.lastLoginAt)}
          </div>
        </div>
      </div>

      {/* Quick Actions — full width */}
      <QuickActions
        pendingReviewCount={pendingReviewCount}
        myDraftCount={myDraftCount}
        activeMissionCount={activeMissionCount}
      />

      {/* Main 2-column layout */}
      <div className="dashboard-home-layout">
        <div className="dashboard-main">
          {/* Infrastructure KPIs */}
          <div className="dashboard-section">
            <h3 className="section-title">{t('dashboard.infrastructure')} <HelpTooltip text="Infrastructure shows your nation's available transport assets, treatment facilities, and UCPM-classified exit/transit hubs." /></h3>
            <div className="infra-kpi-grid">
              <div className="infra-kpi-card" onClick={() => navigate('/assets')}>
                <div className="infra-kpi-icon infra-kpi-icon--primary"><Truck size={22} /></div>
                <div className="infra-kpi-value">{data.infrastructure.assets}</div>
                <div className="infra-kpi-label">{t('dashboard.transportAssets')}</div>
              </div>
              <div className="infra-kpi-card" onClick={() => navigate('/hospital')}>
                <div className="infra-kpi-icon infra-kpi-icon--success"><Building2 size={22} /></div>
                <div className="infra-kpi-value">{data.infrastructure.treatmentFacilities}</div>
                <div className="infra-kpi-label">{t('dashboard.treatmentFacilities')}</div>
              </div>
              <div className="infra-kpi-card" onClick={() => navigate('/startpoints')}>
                <div className="infra-kpi-icon infra-kpi-icon--info"><Plane size={22} /></div>
                <div className="infra-kpi-value">{data.infrastructure.ucpmExitHubs}</div>
                <div className="infra-kpi-label">{t('dashboard.ucpmExitHubs')}</div>
              </div>
              <div className="infra-kpi-card infra-kpi-card-static">
                <div className="infra-kpi-icon infra-kpi-icon--accent"><Ship size={22} /></div>
                <div className="infra-kpi-value">{data.infrastructure.ucpmTransitHubs}</div>
                <div className="infra-kpi-label">{t('dashboard.ucpmTransitHubs')}</div>
              </div>
            </div>
          </div>

          {/* Request Statistics Matrix */}
          <div className="dashboard-section">
            <div className="section-title-row">
              <h3 className="section-title">{t('dashboard.requestStatistics')} <HelpTooltip text="Matrix showing all requests by category and status. Click a cell to filter the requests list." /></h3>
              <span className="section-title-action" onClick={() => navigate('/requests')}>{t('dashboard.viewAllRequests')} <ChevronRight size={14} /></span>
            </div>
            <div className="request-matrix-container">
              <table className="dashboard-table request-matrix">
                <thead>
                  <tr>
                    <th className="matrix-category-header">{t('common.category')}</th>
                    {data.statuses.map(status => (
                      <th key={status} className="matrix-status-header">
                        {getStatusLabel(status)}
                      </th>
                    ))}
                    <th className="matrix-total-header">{t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map(category => {
                    const catTotal = getCategoryTotal(category);
                    return (
                      <tr key={category}>
                        <td className="matrix-category">
                          {getCategoryLabel(category)}
                          {category === 'MEDEVAC' && <HelpTooltip text="Medical Evacuation requests for patient transport and treatment abroad." size={12} />}
                        </td>
                        {data.statuses.map(status => {
                          const cell = data.requestMatrix[category]?.[status];
                          const count = cell?.count || 0;
                          const patients = cell?.patients || 0;
                          return (
                            <td
                              key={status}
                              className={`matrix-cell ${count > 0 ? 'has-value clickable' : ''} ${count >= 5 ? 'heat-high' : count >= 2 ? 'heat-med' : ''}`}
                              onClick={() => count > 0 ? navigate(`/requests?category=${category}&status=${status}`) : undefined}
                              title={count > 0 ? `${t('dashboard.requestCount', { count })} — ${t('dashboard.clickToView')}` : ''}
                            >
                              {count > 0 ? (
                                <div className="matrix-cell-content">
                                  <span className="matrix-count">{count}</span>
                                  {category === 'MEDEVAC' && patients > 0 && (
                                    <span className="matrix-patients">({patients}p)</span>
                                  )}
                                </div>
                              ) : (
                                <span className="matrix-empty">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="matrix-cell matrix-total">
                          <div className="matrix-cell-content">
                            <span className="matrix-count">{catTotal.count}</span>
                            {category === 'MEDEVAC' && catTotal.patients > 0 && (
                              <span className="matrix-patients">({catTotal.patients}p)</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="matrix-footer-row">
                    <td className="matrix-category matrix-footer-label">{t('common.total')}</td>
                    {data.statuses.map(status => (
                      <td key={status} className="matrix-cell matrix-footer">
                        <span className="matrix-count">{getStatusTotal(status)}</span>
                      </td>
                    ))}
                    <td className="matrix-cell matrix-total matrix-footer">
                      <span className="matrix-count">{getGrandTotal()}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Available Assets */}
          {data.assets.length > 0 && (
            <div className="dashboard-section">
              <h3 className="section-title">{t('dashboard.availableAssets')}</h3>
              <div className="assets-table-container">
                <table className="dashboard-table assets-table">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>{t('common.type')}</th>
                      <th>{t('dashboard.seated')}</th>
                      <th>{t('dashboard.lying')}</th>
                      <th>{t('dashboard.intensive')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.map(asset => (
                      <tr key={asset.id}>
                        <td className="asset-name">{asset.name}</td>
                        <td>
                          <span className="asset-type-badge">{getAssetTypeLabel(asset.type)}</span>
                        </td>
                        <td className="capacity-cell">{asset.capacitySeated}</td>
                        <td className="capacity-cell">{asset.capacityLying}</td>
                        <td className="capacity-cell">{asset.capacityIntensive}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Notifications Sidebar */}
        <div className="dashboard-sidebar">
          <div className="news-sidebar">
            <div className="sidebar-header">
              <h3 className="sidebar-title" onClick={() => navigate('/notifications')} style={{ cursor: 'pointer' }} title={t('notifications.viewAll')}><Bell size={16} /> {t('dashboard.notifications')}</h3>
              {notifications.some(n => !n.isRead) && (
                <button
                  className="sidebar-markread-btn"
                  onClick={markAllNotificationsRead}
                  title={t('dashboard.markAllRead')}
                >
                  {t('dashboard.markAllRead')}
                </button>
              )}
              <button
                className="sidebar-settings-btn"
                onClick={openNotifSettings}
                title={t('notifications.settings')}
              >
                <SettingsIcon size={14} />
              </button>
            </div>
            <div className="news-list">
              {(() => {
                const sorted = [...notifications].sort((a, b) =>
                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                );

                if (sorted.length === 0) {
                  return (
                    <div className="news-empty">
                      <Bell size={28} strokeWidth={1.5} style={{ opacity: 0.3, marginBottom: '8px' }} />
                      <div>{t('dashboard.noNotifications')}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', marginTop: '4px', color: 'var(--text-muted)' }}>
                        {t('dashboard.notifiedWhenActivity')}
                      </div>
                    </div>
                  );
                }

                return sorted.map(notif => (
                  <div
                    key={notif.id}
                    className={`notif-item ${!notif.isRead ? 'notif-unread' : ''} ${expandedNotification === notif.id ? 'notif-expanded' : ''}`}
                    onClick={() => {
                      if (expandedNotification === notif.id) {
                        setExpandedNotification(null);
                      } else {
                        setExpandedNotification(notif.id);
                        if (!notif.isRead) {
                          markNotificationRead(notif.id);
                        }
                      }
                    }}
                  >
                    <div className="notif-row">
                      <span className="notif-icon">
                        {NOTIFICATION_ICONS[notif.type] || <Mail size={14} />}
                      </span>
                      <div className="notif-content">
                        <div className="notif-title">{notif.title}</div>
                        <div className="notif-time">
                          <UTCTime date={notif.createdAt} format="dd MMM HH:mm" />
                        </div>
                      </div>
                      {!notif.isRead && <span className="notif-dot" />}
                    </div>
                    {expandedNotification === notif.id && (
                      <div className="notif-detail">
                        <div className="notif-message">{notif.message}</div>
                        {notif.link && (
                          <button
                            className="notif-link-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(notif.link!);
                            }}
                          >
                            {t('dashboard.viewDetailsBtn')} <ExternalLink size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Full-width bottom sections */}
      {/* Nation Users — ADMIN/EXCON only */}
      {(user?.isAdmin || user?.isExcon) && (
        <div className="dashboard-section">
          <h3 className="section-title">{t('dashboard.nationUsers')}</h3>
          <div className="users-table-container">
            <table className="dashboard-table users-table">
              <thead>
                <tr>
                  <th>{t('dashboard.user')}</th>
                  <th>{t('common.email')}</th>
                  <th>{t('dashboard.roles')}</th>
                  <th>{t('dashboard.lastLogin')}</th>
                </tr>
              </thead>
              <tbody>
                {data.nationUsers.map(nu => (
                  <tr key={nu.id}>
                    <td className="user-name-cell">
                      {nu.flagImage && (
                        <img src={nu.flagImage} alt="" className="user-flag-small" />
                      )}
                      {nu.username || '-'}
                    </td>
                    <td className="user-email-cell">{nu.email}</td>
                    <td className="user-roles-cell">
                      {getRoleBadges(nu).map(role => (
                        <span key={role} className="role-badge-small">{role}</span>
                      ))}
                    </td>
                    <td className="user-login-cell">{formatLastLogin(nu.lastLoginAt)}</td>
                  </tr>
                ))}
                {data.nationUsers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty-cell">{t('dashboard.noUsersFound')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Participating Nations */}
      {participatingNations.length > 0 && (
        <div className="dashboard-section">
          <div className="section-title-row">
            <h3 className="section-title"><Globe size={16} /> {t('dashboard.participatingNations')}</h3>
            <span className="section-badge">{participatingNations.length}</span>
          </div>
          <div className="nations-grid">
            {participatingNations.map(nation => (
              <div key={nation.code} className="nation-card" title={nation.name}>
                <img
                  src={nation.flagImage}
                  alt={nation.name}
                  className="nation-flag"
                />
                <span className="nation-name">{nation.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact Cards */}
      <div className="helpdesk-row">
        <div className="dashboard-section helpdesk-card">
          <div className="helpdesk-content">
            <div className="helpdesk-icon">
              <Users size={20} />
            </div>
            <div className="helpdesk-info">
              <h3>{helpdesk.title}</h3>
              <div className="helpdesk-details">
                <span>{helpdesk.email}</span>
                {helpdesk.phone && <><span className="helpdesk-separator">|</span><span>{helpdesk.phone}</span></>}
                {helpdesk.info && <><span className="helpdesk-separator">|</span><span>{helpdesk.info}</span></>}
              </div>
            </div>
          </div>
        </div>
        <div className="dashboard-section helpdesk-card reallife-card">
          <div className="helpdesk-content">
            <div className="helpdesk-icon">
              <Heart size={20} />
            </div>
            <div className="helpdesk-info">
              <h3>{reallifeSupport.title}</h3>
              <div className="helpdesk-details">
                {reallifeSupport.email && <span>{reallifeSupport.email}</span>}
                {reallifeSupport.phone && <><span className="helpdesk-separator">|</span><span>{reallifeSupport.phone}</span></>}
                {reallifeSupport.info && <><span className="helpdesk-separator">|</span><span>{reallifeSupport.info}</span></>}
                {!reallifeSupport.email && !reallifeSupport.phone && !reallifeSupport.info && <span>{t('dashboard.notConfigured')}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Disclaimer */}
      <div className="exercise-disclaimer">
        <div className="exercise-disclaimer-icon"><Info size={18} /></div>
        <div className="exercise-disclaimer-text">
          {t('dashboard.exerciseDisclaimer')}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
